import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowRight, CalendarClock, ListTree, Wrench } from "lucide-react";
import { api } from "../lib/api";
import { useMeta, injectJsonLd, removeJsonLd } from "../lib/seo";
import { breadcrumbJsonLd } from "../seoShared";
import { setSpanishAvailable } from "../lib/esGate";
import { Button } from "../components/ui/button";

// Guide page renderer — long-form reading design: capped measure, real hierarchy,
// step cards, warning callouts, sticky TOC on desktop, what's-next block.
function esc(s) { return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;"); }
function inline(s) {
  return esc(s)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/(?!\/)[^)\s]*)\)/g, '<a href="$2" class="text-emerald-700 font-medium hover:underline">$1</a>');
}
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const isWarningSection = (h) => /watch out|mistake|avoid|don't|warning/i.test(h);
const isDeadlineSection = (h) => /deadline|window|timeline/i.test(h);

function parseBlocks(content) {
  const blocks = [];
  let section = "";
  let inCode = false;
  let codeLines = [];
  for (const raw of content.split("\n")) {
    const t = raw.trim();
    if (t.startsWith("```")) {
      if (inCode) { blocks.push({ type: "code", text: codeLines.join("\n") }); codeLines = []; inCode = false; }
      else inCode = true;
      continue;
    }
    if (inCode) { codeLines.push(raw); continue; }
    if (!t) continue;
    if (t.startsWith("### ")) { blocks.push({ type: "h3", text: t.slice(4) }); continue; }
    if (t.startsWith("## ")) {
      const text = t.slice(3);
      section = text;
      blocks.push({ type: "h2", text, id: slugify(text), warning: isWarningSection(text), deadline: isDeadlineSection(text) });
      continue;
    }
    if (t.startsWith("# ")) { blocks.push({ type: "h2", text: t.slice(2), id: slugify(t.slice(2)), warning: isWarningSection(t.slice(2)), deadline: isDeadlineSection(t.slice(2)) }); section = t.slice(2); continue; }
    const stepM = t.match(/^(\d+)\.\s+(.*)$/);
    if (stepM) { blocks.push({ type: "step", n: stepM[1], text: stepM[2], warning: isWarningSection(section), deadline: isDeadlineSection(section) }); continue; }
    if (t.startsWith("- ")) { blocks.push({ type: "li", text: t.slice(2), warning: isWarningSection(section), deadline: isDeadlineSection(section) }); continue; }
    blocks.push({ type: "p", text: t, deadline: isDeadlineSection(section) });
  }
  return blocks;
}

function Block({ b, i }) {
  if (b.type === "code") {
    return (
      <pre key={i} className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-5 text-sm leading-relaxed text-slate-800 whitespace-pre-wrap font-mono overflow-x-auto" data-testid={`guide-template-${i}`}>{b.text}</pre>
    );
  }
  if (b.type === "h2") {
    return <h2 key={i} id={b.id} className="text-2xl font-extrabold text-slate-900 mt-12 mb-4 tracking-tight scroll-mt-24" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
  }
  if (b.type === "h3") {
    return <h3 key={i} className="text-lg font-bold text-slate-800 mt-8 mb-2" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
  }
  if (b.type === "step") {
    return (
      <div key={i} className="flex gap-4 mt-4 items-start">
        <span className="w-7 h-7 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center shrink-0 mt-0.5">{b.n}</span>
        <p className="text-lg leading-relaxed text-slate-700 flex-1" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />
      </div>
    );
  }
  if (b.type === "li" && b.warning) {
    return (
      <div key={i} className="flex gap-3 mt-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 items-start" data-testid={`guide-warning-${i}`}>
        <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-1" />
        <p className="text-base leading-relaxed text-amber-900" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />
      </div>
    );
  }
  if (b.type === "li") {
    return (
      <div key={i} className="flex gap-3 mt-2.5 items-start">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 mt-3" />
        <p className="text-lg leading-relaxed text-slate-700 flex-1" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />
      </div>
    );
  }
  if (b.type === "p" && b.deadline) {
    return (
      <div key={i} className="mt-4 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-xl px-5 py-4 flex gap-3 items-start" data-testid={`guide-deadline-${i}`}>
        <CalendarClock size={18} className="text-emerald-600 shrink-0 mt-1" />
        <p className="text-lg leading-relaxed text-emerald-950" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />
      </div>
    );
  }
  return <p key={i} className="text-lg leading-relaxed text-slate-700 mt-5" dangerouslySetInnerHTML={{ __html: inline(b.text) }} />;
}

export default function GuidePage() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);
  useMeta(page ? `${page.title} | Denial Decoder` : "Guide | Denial Decoder", page?.description || "");
  useEffect(() => {
    setPage(null);
    setMissing(false);
    api.get(`/guide/pages/${slug}`, { params: { lang: i18n.language === "es" ? "es" : "en" } })
      .then((r) => setPage(r.data))
      .catch(() => setMissing(true));
  }, [slug, i18n.language]);
  useEffect(() => {
    if (page) setSpanishAvailable(!!page.has_es);
    return () => setSpanishAvailable(true);
  }, [page]);
  useEffect(() => {
    if (page?.faqs?.length) {
      injectJsonLd("faq-schema", { "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: page.faqs.map((f) => ({ "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a } })) });
      return () => removeJsonLd("faq-schema");
    }
  }, [page]);
  useEffect(() => {
    if (!page) return;
    injectJsonLd("breadcrumb-schema", breadcrumbJsonLd(`guide/${page.slug}`, page.title, "guide", "Guide"));
    return () => removeJsonLd("breadcrumb-schema");
  }, [page]);

  const blocks = useMemo(() => (page ? parseBlocks(page.content) : []), [page]);
  const toc = useMemo(() => blocks.filter((b) => b.type === "h2"), [blocks]);
  const words = useMemo(() => (page ? page.content.split(/\s+/).length : 0), [page]);
  // Key takeaway: the first real paragraph (our content rules guarantee it's the direct answer).
  const takeawayIdx = useMemo(() => blocks.findIndex((b) => b.type === "p"), [blocks]);

  if (missing) {
    return <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="guide-missing">
      <p className="text-slate-600">That guide page isn&rsquo;t published yet.</p>
      <Link to="/guide" className="text-emerald-700 font-medium hover:underline">Browse the guide</Link>
    </div>;
  }
  if (!page) return <div className="max-w-3xl mx-auto px-4 py-20 text-slate-500">Loading…</div>;

  const axisLabel = { "situation": "Situations", "denial-reason": "Denial reasons", "claim-type": "Claim types", "reference": "Reference", "write-it-yourself": "Write it yourself" }[page.axis] || "Guide";
  return (
    <div className="max-w-6xl mx-auto px-4 py-10 lg:grid lg:grid-cols-[220px_1fr] lg:gap-12" data-testid={`guide-page-${page.slug}`}>
      {words > 800 && toc.length > 1 && (
        <aside className="hidden lg:block" data-testid="guide-toc">
          <div className="sticky top-24">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5"><ListTree size={13} /> On this page</p>
            <nav className="space-y-2 border-l-2 border-slate-100">
              {toc.map((h) => (
                <a key={h.id} href={`#${h.id}`} className="block pl-3 text-sm text-slate-500 hover:text-emerald-700 hover:border-emerald-500 -ml-0.5 border-l-2 border-transparent transition-colors" data-testid={`guide-toc-${h.id}`}>{h.text}</a>
              ))}
            </nav>
          </div>
        </aside>
      )}
      <article className="max-w-[70ch]">
        <nav className="text-xs text-slate-500 mb-6" data-testid="guide-breadcrumbs">
          <Link to="/guide" className="hover:text-emerald-700">Guide</Link>
          <span className="mx-1.5">/</span>{axisLabel}<span className="mx-1.5">/</span>
          <span className="text-slate-600">{page.title}</span>
        </nav>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-8 leading-tight tracking-tight">{page.title}</h1>

        {takeawayIdx >= 0 && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-4" data-testid="guide-key-takeaway">
            <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">The short version</p>
            <p className="text-lg leading-relaxed text-emerald-950" dangerouslySetInnerHTML={{ __html: inline(blocks[takeawayIdx].text) }} />
          </div>
        )}

        <div data-testid="guide-content">
          {blocks.map((b, i) => (i === takeawayIdx ? null : <Block key={i} b={b} i={i} />))}
        </div>

        {(page.related?.length > 0 || true) && (
          <div className="mt-14 border-t-2 border-slate-200 pt-8" data-testid="guide-whats-next">
            <h2 className="text-xl font-extrabold text-slate-900 mb-5">What&rsquo;s next</h2>
            {page.related?.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="guide-related">
                {page.related.map((r) => (
                  <Link key={r} to={`/guide/${r}`} className="bg-white border border-slate-200 rounded-xl p-4 hover:border-emerald-400 hover:shadow-sm transition-shadow group" data-testid={`guide-related-${r}`}>
                    <p className="font-semibold text-sm text-slate-900 group-hover:text-emerald-700 capitalize flex items-center justify-between gap-2">
                      {r.replace(/-/g, " ")} <ArrowRight size={14} className="text-emerald-600 shrink-0" />
                    </p>
                  </Link>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link to="/tools" className="bg-slate-50 border border-slate-200 rounded-xl p-4 hover:border-emerald-400 transition-colors" data-testid="guide-next-tool">
                <Wrench size={16} className="text-emerald-600 mb-1.5" />
                <p className="font-semibold text-sm text-slate-900">Free tools</p>
                <p className="text-xs text-slate-500 mt-0.5">Deadlines, denial reasons, regulators, call prep.</p>
              </Link>
              <div className="bg-emerald-600 rounded-xl p-4 text-white" data-testid="guide-tool-handoff">
                <p className="font-semibold text-sm">Want us to check your specific denial?</p>
                <Link to="/check"><Button size="sm" className="mt-2 rounded-full bg-white text-emerald-800 hover:bg-emerald-50" data-testid="guide-cta">Decode my denial — free</Button></Link>
              </div>
            </div>
          </div>
        )}

        {page.faqs?.length > 0 && (
          <div className="mt-12 border-t border-slate-200 pt-8" data-testid="guide-faqs">
            <h2 className="text-xl font-extrabold text-slate-900 mb-4">Common questions</h2>
            {page.faqs.map((f, i) => (
              <details key={i} className="border-b border-slate-100 py-4" data-testid={`guide-faq-${i}`}>
                <summary className="text-base font-semibold text-slate-800 cursor-pointer">{f.q}</summary>
                <p className="text-base leading-relaxed text-slate-600 mt-2">{f.a}</p>
              </details>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500 mt-10">General information, not legal advice. For advice about your situation, consult a licensed attorney in your state.</p>
      </article>
    </div>
  );
}
